import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ISSUERS } from 'src/app/constants/contact';
import * as words from '../../../../../../services/language';
import { MasterService } from "../../../../../../services/master.service";
import { MessageService } from "../../../../../../services/message.service";
import { StoreService } from "../../../../../../services/store.service";

import { MIN_SEARCH_CHARS, SCROLL_BOTTOM_OUT, SEARCH_DEBOUNCE_MILLIS } from "src/app/constants/list";
import { SecurityService } from "src/app/services/security.service";
import { Contact } from "src/app/models/contact.model";
import { BuyerType } from "src/app/models/buyer-type.model";
import { IdentificationType } from "src/app/models/identification-type.model";
import { Address } from "src/app/models/address.model";
import { ContactRole } from "src/app/models/contact-role.model";
import { Subscription } from 'rxjs';

@Component({
    selector: "contact-list",
    templateUrl: "./contact-list.component.html",
    styleUrls: ["./contact-list.component.scss"],
    standalone: false
})
export class ContactListComponent implements OnInit, OnChanges, OnDestroy {

    /** Filter the list by the given saleId so to help prevent adding the same contact twice. */
    @Input() saleId: number = 0

    /** Indicates if the parent has asked this component to how/hide itself. */
    @Input() show: boolean = true

    /** Notify listeners that a contact was selected from the list or a new one was created. */
    @Output() contactSelectedEvent = new EventEmitter<Number>()

    // zip code list
    protected zipCodeList: Address[] = []
    
    // latest search inquiry
    protected zipInquiry = null
    
    // set time out
    private timeOutID: any;
    
    protected issuers = ISSUERS
    
    protected buyerTypes: BuyerType[] = []
    
    protected buyerTypeId: number | null = null

    protected types: IdentificationType[] = []

    protected roles: ContactRole[] = []

    // save the language
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = words.language

    // Indicates whether the current contact type being displayed is an individual or a company (false)
    protected isIndividual: boolean = true;

    // define the contact type (title)

    // save all data by type
    protected contacts: Contact[] = [];

    // define if is loading
    protected loading: boolean = false;

    // Current page the user is viewing (last loaded).
    protected page = 1

    // The number contacts currently being displayed.
    protected displayed = 0

    // Indicates whether or not there are no more records to load.
    protected exhausted = false

    // Total number of records available
    protected records: number

    // The search form group
    protected form: FormGroup | null = null

    // The current search text to filter the list by.
    private inquiry: string | null = null

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    // Keep track of how the user wants to sort list.
    private sortables: Object = { 'name': 'ASC' }

    private subscriptions: Subscription[] = []
    
    public saleBuyerInformation = new FormGroup({
        isIndividual: new FormControl<boolean>(true, []),
        taxPayerNumber: new FormControl<string | null>(null),
        identificationTypeId: new FormControl<number | null>(null, []),
        number: new FormControl<string | null>(null, []),
        issuer: new FormControl<string | null>(null, []),
        expirationDate: new FormControl<string | null>(null, []),
        firstName: new FormControl<string | null>(null),
        lastName: new FormControl<string | null>(null),
        middleName: new FormControl<string | null>(null),
        companyName: new FormControl<string | null>(null),
        address: new FormControl<string | null>(null, [Validators.required]),
        address2: new FormControl<string | null>(null),
        city: new FormControl<string | null>({ value: null, disabled: true }, [Validators.required]),
        state: new FormControl<string | null>({ value: null, disabled: true }, [Validators.required]),
        zipCode: new FormControl<string | null>(null, [Validators.required]),
        county: new FormControl<string | null>({ value: null, disabled: true }, []),
        country: new FormControl<string | null>({ value: null, disabled: true }, []),
        phoneNumber: new FormControl<string | null>(null),
        emailAddress: new FormControl<string | null>(null),
        roles: new FormControl<string>('salesLead', [Validators.required])
    })
    
    constructor(private ms: MessageService, private master: MasterService, private store: StoreService, private security: SecurityService, private fb: FormBuilder) {
        this.form = this.fb.group({
            inquiry: new FormControl<string>('')
        })
    }

    ngOnInit(): void {
        this.getTypes()

        this.subscriptions.push(
            this.form.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                if (this.inquiry === inquiry) {
                    return
                }

                this.watcher = setTimeout(() => {
                    this.inquiry = inquiry
                    this.reset()
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.saleId) {
            this.reset()
            this.fetch()
        }

        if (changes.show) {
            if (!this.show) {
                return
            }

            this.reset()
            this.fetch()
            this.setActiveTab('contact-find')
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
     */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    private resetBuyer = () => {
        this.saleBuyerInformation.reset()
        this.saleBuyerInformation.get('isIndividual').setValue(true)
        this.saleBuyerInformation.get('roles').setValue('salesLead')
    }

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    public scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
     * 
     * @param field The field that user wants to sort by. 
     */
    public sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        field = field.toLowerCase()
        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    public sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        field = field.toLowerCase()
        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    public sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        field = field.toLowerCase()
        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    /**
     * Acquire contact list by given type
     */
    public fetch = (): void => {
        if (this.security.notPermitted('contacts', 'contact list', 'view')) {
            return
        }

        if (this.loading) { // already getting next page
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        this.loading = true

        let inquiryParam = ''
        if (this.inquiry && (this.inquiry.length > 0)) {
            inquiryParam = `&q=${encodeURIComponent(this.inquiry)}`
        }

        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }

                sorts.push(`${key}${direction}`)
            }

            sortParam = `&sort=${sorts.join(',')}`
        }

        let notSaleParam = ''
        if (this.saleId && (this.saleId > 0)) {
            notSaleParam = `&notSaleId=${this.saleId}`
        }

        this.master.get(`contacts?page=${this.page}${inquiryParam}${notSaleParam}${sortParam}`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.contacts = this.contacts.concat(res.data.contacts)
                this.displayed = this.contacts.length
                return
            }

            this.contacts = res.data.contacts;
            this.displayed = this.contacts.length
        })
    }

    /*
    todo: get types of identifications
    */
    private getTypes = (): void => {
        this.master.get('collections/contacts/identificationTypes', res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
            }

            this.types = res.data.IdentificationTypes
        })

        this.master.get(`collections/sales/buyertypes`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
            }

            this.buyerTypes = res.data.BuyerTypes ? res.data.BuyerTypes.slice() : []
            
            const defaultType = this.buyerTypes.find(item => item.isIndividual == true)
            if (!defaultType) {
                return
            }

            this.buyerTypeId = defaultType.id
        })
    }

    // todo: listen to output of navbar
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break;
            default:
                break;
        }
    }

    // ? address lookup
    // verify if the corporate is already exist
    public searchZipCode(): void {
        const code = this.saleBuyerInformation.get('zipCode').value
        if (!code || code.length < MIN_SEARCH_CHARS) {
            return
        }

        if (this.zipInquiry && (code === this.zipInquiry)) { // hasn't changed?
            return
        }

        clearTimeout(this.timeOutID)

        this.timeOutID = setTimeout(() => {
            this.zipCodeList = []
            this.zipInquiry = code

            this.master.get(`readLocationByZipcode?zipcode=${code}`, res => {
                if (!res) {
                    this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                    return
                }

                if (res.status !== 200) {
                    return
                }

                this.zipCodeList = res.data.locations

                // autocomplete
                if ((code.length >= 5) && (this.zipCodeList.length === 1)) {
                    this.selectZipCode(this.zipCodeList[0])
                }
            })
        }, SEARCH_DEBOUNCE_MILLIS)
    }

    // set the address exist
    public selectZipCode = (address: Object): void => {
        if (!address || !this.saleBuyerInformation) {
            return
        }

        this.saleBuyerInformation.get('city').setValue(address['city'])
        this.saleBuyerInformation.get('state').setValue(address['state'])
        this.saleBuyerInformation.get('county').setValue(address['county'])
        this.saleBuyerInformation.get('zipCode').setValue(address['zipcode'])
        this.saleBuyerInformation.get('country').setValue('United States')

        this.zipCodeList = []
    }

    public contactSelected = (id): void => {
        this.form.get('inquiry').setValue('', {emitEvent: false})
        this.inquiry = null

        this.contactSelectedEvent.emit(id)
    }

    public updateIsIndividual(e) {
        if (e.target.value !== "1") {
            this.saleBuyerInformation.get("isIndividual").setValue(false)
            this.isIndividual = false
            this.modifyValidators()
            return
        }

        this.saleBuyerInformation.get("isIndividual").setValue(true)
        this.isIndividual = true
        this.modifyValidators()
    }

    public saveContact(){
        if (this.saleBuyerInformation.invalid) {
            return
        }

        this.createContact()
    }

    public createContact() {
        if (this.saleBuyerInformation.invalid){
            return
        }
        
        const information = this.saleBuyerInformation.getRawValue()
        if (information.roles && (information.roles as unknown as string[]).length > 0) {
            const role = information.roles as unknown as string
            ;(information.roles as unknown as string[]) = [role]
        }

        this.master.post(`contacts/quick`, { information: information }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
            }

            if (!res.data.contact) {
                return
            }

            const contactId = parseInt(res.data.contact['id'])
            this.contactSelected(contactId)
            
            this.isIndividual = true
            this.modifyValidators()
            this.form.reset()
            this.resetBuyer()
        })
    }

    /**
     * Select the active tab based on the value given.
     * @param activeTab The name of the active tab to select.
     */
    protected setActiveTab = (activeTab: string) => {
        const links: NodeListOf<HTMLAnchorElement> = document.querySelectorAll<HTMLAnchorElement>('.nav-tabs > .nav-item > a.nav-link')
        links.forEach((link) => {
            link.classList.remove('active')
        })

        const tabs: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>('.tab-content > div.tab-pane')
        tabs.forEach((tab) => {
            tab.classList.remove('active')
            tab.classList.remove('show')
        })

        const tabLink: HTMLAnchorElement = document.getElementById(`${activeTab}-link`) as HTMLAnchorElement
        if (tabLink) {
            tabLink.classList.add('active')
        }

        const tab: HTMLElement = document.getElementById(`${activeTab}`) as HTMLElement
        if (tab) {
            tab.classList.add('active')
            tab.classList.add('show')
        }
    }

    public modifyValidators() {
        this.saleBuyerInformation.clearValidators()
        if (this.isIndividual) {
            this.saleBuyerInformation.get('firstName').setValidators([Validators.required])
            this.saleBuyerInformation.get('lastName').setValidators([Validators.required])
            this.saleBuyerInformation.get('companyName').setValidators(null)
        }
        else {
            this.saleBuyerInformation.get('companyName').setValidators([Validators.required])
        }

        this.saleBuyerInformation.get('firstName').updateValueAndValidity()
        this.saleBuyerInformation.get('lastName').updateValueAndValidity()
        this.saleBuyerInformation.get('taxPayerNumber').updateValueAndValidity()
        this.saleBuyerInformation.get('companyName').updateValueAndValidity()
    }
}
